import { SavedPlace } from '@ahamove/icons/map';
import { color } from '@ahamove/tokens';
import { useAuth } from '@ahm/react-auth';
import { useLocationStore } from '@ahm/react-map/store';
import {
  Avatar,
  Badge,
  Button,
  Combobox,
  Divider,
  Group,
  Stack,
  Text,
  ThemeIcon,
  rem,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Plus } from 'lucide-react';
import classes from '../address-autocomplete.module.css';
import { AddressAutocompleteItem } from '../autocomplete-item';
import { SavedPlaceForm } from '../form';
import { createTranslations, locales } from '../locales';
import AllSavedPlaceModal from '../modals/all-saved-place.modal';
import { AutocompleteModalId } from '../modals/constants';
import useAutocompleteModal from '../modals/use-autocomplete-modal';
import type { SavedAddressOptionsProps } from '../types';
import { useGetUserPlace } from '@/components/address-autocomplete/form/use-get-user-place';
import { QuickAccessAIChatButton } from '@/features/AI';
import { MAX_NUMBER_OF_USER_PLACE } from '@/lib/constants';

const SavedAddressOptions = ({
  lang = 'vi',
  pathIndex,
}: SavedAddressOptionsProps) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const t = createTranslations({ locales, lang });
  const { openModal } = useAutocompleteModal();
  const options = useLocationStore.use.savedLocations();

  const { status } = useAuth();
  const { data: userPlaces } = useGetUserPlace({
    enabled: status === 'authenticated',
  });

  const list = isMobile ? (
    <>
      <Divider color="neutral.0" />
      <Stack gap={0} bg="white">
        {options.map((o, idx) => {
          return (
            <Combobox.Option
              className={classes.option}
              title={o.address}
              value={o.address}
              key={`${idx}_${!o.lat || !o.lng ? `${o.lat}_${o.lng}` : o.place_id}`}
            >
              <AddressAutocompleteItem
                primary={o.name!}
                secondary={o.address}
                icon={<SavedPlace width={16} height={16} />}
              />
            </Combobox.Option>
          );
        })}
      </Stack>
    </>
  ) : (
    <Group
      px="md"
      pb="md"
      styles={{
        root: {
          rowGap: rem(8),
          columnGap: rem(12),
        },
      }}
    >
      {<QuickAccessAIChatButton border="1px solid #6E5CCA" />}
      {options.slice(0, 6).map((o) => (
        <Combobox.Option
          title={o.name}
          value={o.address}
          key={o.code}
          p={0}
          className={classes['unstyled-option']}
          style={{ cursor: 'pointer' }}
        >
          <Badge
            py={rem(10)}
            px={rem(12)}
            radius="xl"
            maw={124}
            h={'auto'}
            c="primary"
            tt={'none'}
            fz="h6"
            style={{
              borderColor: 'var(--mantine-color-primary-6)',
              backgroundColor: 'var(--mantine-color-primary-0)',
              fontWeight: 600,
            }}
            leftSection={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="12"
                viewBox="0 0 10 12"
                fill="none"
              >
                <path
                  d="M10 11.3989C10.0002 11.5061 9.97052 11.6113 9.91399 11.7038C9.85745 11.7962 9.77615 11.8724 9.67853 11.9245C9.58091 11.9766 9.47054 12.0026 9.35891 11.9998C9.24727 11.997 9.13845 11.9656 9.04375 11.9088L5.33125 9.67703C5.23192 9.61744 5.11714 9.58584 5 9.58584C4.88286 9.58584 4.76808 9.61744 4.66875 9.67703L0.956251 11.9088C0.861554 11.9656 0.752728 11.997 0.641093 11.9998C0.529458 12.0026 0.419092 11.9766 0.321473 11.9245C0.223854 11.8724 0.142549 11.7962 0.0860134 11.7038C0.029478 11.6113 -0.000222492 11.5061 1.2549e-06 11.3989V1.19988C1.2549e-06 0.881652 0.131697 0.576458 0.366118 0.351437C0.600538 0.126415 0.91848 0 1.25 0H8.75C9.08152 0 9.39946 0.126415 9.63388 0.351437C9.8683 0.576458 10 0.881652 10 1.19988V11.3989Z"
                  fill={color.primary[60]}
                />
              </svg>
            }
            styles={{
              section: {
                gap: rem(10),
              },
            }}
          >
            {o.name}
          </Badge>
        </Combobox.Option>
      ))}
      {userPlaces && userPlaces.length < MAX_NUMBER_OF_USER_PLACE && (
        <Button
          radius="xl"
          size="sm"
          color="primary"
          variant="outline"
          style={{
            borderColor: 'var(--mantine-color-primary-6)',
            backgroundColor: 'var(--mantine-color-primary-0)',
          }}
          leftSection={<Plus size={20} strokeWidth={2} />}
          onClick={() => {
            openModal({
              modalId: AutocompleteModalId.NEW_SAVED_PLACED,
              modalContent: <SavedPlaceForm pathIndex={pathIndex} />,
            });
          }}
        >
          {t('add_new')}
        </Button>
      )}
    </Group>
  );

  return (
    <Stack gap={isMobile ? 0 : rem(8)} bg="white">
      <Group px="md" py={isMobile ? 'sm' : 'md'} justify="space-between">
        <Text component="h6" fw={700} fz={rem(16)}>
          {t('save_places')}
        </Text>
        <Button
          fw={700}
          variant="subtle"
          size="compact-sm"
          flex="none"
          mr={-4}
          onClick={() => {
            openModal({
              modalId: AutocompleteModalId.ALL_SAVED_PLACED,
              modalContent: (
                <AllSavedPlaceModal pathIndex={pathIndex} lang={lang} />
              ),
            });
          }}
        >
          {t('see_all')}
        </Button>
      </Group>
      {list}
    </Stack>
  );
};

export default SavedAddressOptions;
